import { layoutToWorld } from '../../../core/coords';
import type { Ring, Vec2 } from '../../../geo/types';
import { ensureCCW } from '../../../geo/polygon';
import { triangulate } from '../../../geo/triangulate';
import { PATTERN } from '../../../render/patterns/patternIds';
import { SURFACE } from '../../mesh/attributes';
import type { MeshBuilder, VertexStyle } from '../../mesh/meshBuilder';
import { refineTriangles } from '../../terrain/densify';
import { withHeightRefinement } from '../../terrain/heightRefinement';
import { orientedFace } from '../solids';

/** Shallow court soil follows local terrain; it does not raise the precinct on a slab. */
export function buildConventSoil(
  builder: MeshBuilder,
  style: VertexStyle,
  ring: Ring,
  terrain: (p: Vec2) => number,
  base: number,
  height: number,
  garden: boolean
): void {
  ring = ensureCCW(ring);
  const soil = {
      ...style,
      pattern: PATTERN.none,
      extent: [0, 0] as const,
      surface: garden ? SURFACE.garden : SURFACE.street,
    },
    frame = { origin: [0, 0, 0] as const, yaw: 0 },
    refined = withHeightRefinement(undefined, terrain, 0.003),
    y = (p: Vec2) => refined.heightAt(p) - base + height,
    mesh = refineTriangles(triangulate({ outer: ring, holes: [] }), refined.config),
    point = (i: number): Vec2 => [mesh.positions[i * 2]!, mesh.positions[i * 2 + 1]!],
    bottom =
      Math.min(
        ...Array.from({ length: mesh.positions.length / 2 }, (_, i) => refined.heightAt(point(i)))
      ) -
      base -
      0.04,
    edges = new Map<string, { a: number; b: number; count: number }>();
  // Soil patches can be only 15 mm apart: a coarse terrain chord must not cover the higher patch.
  for (let i = 0; i < mesh.indices.length; i += 3) {
    const indices = [mesh.indices[i]!, mesh.indices[i + 1]!, mesh.indices[i + 2]!],
      tri = indices.map(point);
    orientedFace(
      builder,
      soil,
      frame,
      tri.map((p) => layoutToWorld(p, y(p))),
      [0, 1, 0]
    );
    orientedFace(
      builder,
      soil,
      frame,
      tri.map((p) => layoutToWorld(p, bottom)),
      [0, -1, 0]
    );
    for (let j = 0; j < 3; j++) {
      const a = indices[j]!,
        b = indices[(j + 1) % 3]!,
        key = a < b ? `${a}:${b}` : `${b}:${a}`;
      const edge = edges.get(key);
      if (edge) edge.count++;
      else edges.set(key, { a, b, count: 1 });
    }
  }
  // Use the refined boundary for the skirts too, keeping the shallow solid closed on a curved slope.
  for (const edge of edges.values()) {
    if (edge.count !== 1) continue;
    const from = point(edge.a),
      to = point(edge.b);
    orientedFace(
      builder,
      soil,
      frame,
      [
        layoutToWorld(from, bottom),
        layoutToWorld(to, bottom),
        layoutToWorld(to, y(to)),
        layoutToWorld(from, y(from)),
      ],
      [to[1] - from[1], 0, to[0] - from[0]]
    );
  }
}
